// A player's average for the season currently on display.
//
// The API annotates players with display_season / season_games /
// season_fantasy_avg. When a player has no games in the display season it sends
// season_fantasy_avg: null — and three call sites each used to read that as
// "fall back to players.fantasy_points_avg and drop the year label". That static
// column is only as fresh as the last /stats/recalculate, and for players the
// roster sync invented it is not an average at all. So a backup with three good
// games last December showed 10.7 as an unlabelled average next to this
// season's, high enough to earn him a gold "Elite" star.
//
// Nothing here reads fantasy_points_avg. No games means 0.0, said plainly, with
// the games count alongside so the zero can't be mistaken for a bad performance.
import { formatFantasyPoints } from './typeUtils';

/** Fantasy points per game that earn the gold star and the "Elite" flag. */
export const ELITE_AVG_THRESHOLD = 8.5;

/** The season fields the API annotates onto a player. */
export interface SeasonAnnotated {
  display_season?: number;
  season_fantasy_avg?: number | null;
  season_games?: number;
}

export interface SeasonAverage {
  /** Display-season average; 0 when the player has no games in it. */
  avg: number;
  /** Games played in the display season. */
  games: number;
  /** The display season, when the API annotated one. */
  season?: number;
  /** `avg` formatted for display. */
  display: string;
  /** Sub-label, e.g. "2026 Avg · 0 GP". */
  label: string;
  /** False when the player has not played this season — gates Elite badges. */
  hasPlayed: boolean;
}

export function seasonAverage(
  player: SeasonAnnotated | null | undefined,
  fallbackLabel = 'Avg',
): SeasonAverage {
  const season = player?.display_season;
  const games = player?.season_games ?? 0;
  const avg = player?.season_fantasy_avg ?? 0;

  return {
    avg,
    games,
    season,
    display: formatFantasyPoints(avg),
    // Without a display_season the player was never annotated, so we don't know
    // the games count either — claiming "0 GP" there would be a guess.
    label: season ? `${season} ${fallbackLabel} · ${games} GP` : fallbackLabel,
    hasPlayed: games > 0,
  };
}

/** Sort comparator, best display-season average first. */
export function bySeasonAverageDesc(a: SeasonAnnotated, b: SeasonAnnotated): number {
  return seasonAverage(b).avg - seasonAverage(a).avg;
}

/** True when this season's form earns the gold star / "Elite" flag. */
export function isElite(stats: SeasonAverage): boolean {
  return stats.hasPlayed && stats.avg > ELITE_AVG_THRESHOLD;
}
